/**
 * Global DOM-delegated tracking.
 *
 * Handles every click-driven dataLayer event that does NOT originate
 * inside a component that already owns the interaction (FilterSort,
 * AddToCart, NewsletterSignup). Those components import from
 * helpers/analytics.js directly.
 *
 * Loaded once in theme.liquid via Vite.
 */

import {
  trackPedigreeClick,
  trackArClick,
  trackBreezeCta,
  trackRecirculationClick,
  trackBeginCheckout,
  trackFileDownload,
  trackMagazineCategorySelected,
  trackFooterLinkClick,
  trackNavLinkClick,
} from '../helpers/analytics';

function linkText(anchor) {
  return (anchor.textContent || '').replace(/\s+/g, ' ').trim();
}

function init() {
  // ── PDP: See Race Data / Pedigree click ─────────────────────────
  document.querySelectorAll('[data-track-pedigree]').forEach((el) => {
    el.addEventListener('click', () => {
      trackPedigreeClick(
        el.dataset.productName || '',
        el.dataset.horseName || '',
      );
    });
  });

  // ── PDP: AR Experience click ────────────────────────────────────
  document.querySelectorAll('[data-track-ar]').forEach((el) => {
    el.addEventListener('click', () => {
      trackArClick(el.dataset.productName || '');
    });
  });

  // ── PDP: Breeze CTA click ──────────────────────────────────────
  // Accelpay/Breeze badges are injected by a third-party script.
  // Delegate from the nearest static ancestor.
  document.querySelectorAll('[data-track-breeze]').forEach((el) => {
    el.addEventListener('click', () => {
      trackBreezeCta(el.dataset.productName || '');
    });
  });

  // ── PDP: Recirculation click ────────────────────────────────────
  const recirc = document.querySelector('.product-recirculation');
  if (recirc) {
    recirc.addEventListener('click', (e) => {
      const anchor = e.target.closest('a');
      if (!anchor) return;
      const card = anchor.closest('.product-card');
      if (!card) return;
      trackRecirculationClick(card.dataset.productTitle || '');
    });
  }

  // ── Cart: Checkout click ────────────────────────────────────────
  // The cart drawer re-renders on every update, so we delegate from
  // the persistent <cart-drawer> element.
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    cartDrawer.addEventListener('click', (e) => {
      const btn = e.target.closest('#CartDrawer-Checkout');
      if (!btn) return;
      const footer = document.getElementById('main-cart-footer');
      const cartValue = parseInt(footer?.dataset.cartTotal || '0', 10) / 100;
      const itemCount = parseInt(footer?.dataset.cartCount || '0', 10);
      trackBeginCheckout(cartValue, itemCount);
    });
  }

  // ── Trade: Asset download ───────────────────────────────────────
  const tradePage = document.getElementById('main-trade');
  if (tradePage) {
    document.addEventListener('click', (e) => {
      const anchor = e.target.closest('[data-track-download]');
      if (!anchor) return;
      trackFileDownload(
        anchor.dataset.fileName || linkText(anchor),
        anchor.dataset.fileType || '',
        anchor.dataset.productVintage || '',
      );
    });
  }

  // ── Magazine: Category filter click ─────────────────────────────
  document.querySelectorAll('[data-track-category]').forEach((el) => {
    el.addEventListener('click', () => {
      trackMagazineCategorySelected(el.dataset.trackCategory || '');
    });
  });

  // ── Footer links ────────────────────────────────────────────────
  const footer = document.querySelector('#shopify-section-layout--footer');
  if (footer) {
    footer.addEventListener('click', (e) => {
      const anchor = e.target.closest('a');
      if (!anchor) return;
      trackFooterLinkClick(linkText(anchor));
    });
  }

  // ── Navigation links ────────────────────────────────────────────
  const navSections = document.querySelectorAll(
    '.main-navigation, .mobile-navigation',
  );
  navSections.forEach((section) => {
    section.addEventListener('click', (e) => {
      const anchor = e.target.closest('a');
      if (!anchor) return;
      trackNavLinkClick(linkText(anchor));
    });
  });
}

// Self-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
